import { useEffect } from 'react';
import { createFileRoute, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useBinScopedStore } from '@amber-ui/core/src/stores/bin-scoped-store';
import { useAuth } from '@amber-ui/core/src/contexts/auth-context';
import { ContainerHubColorSync } from '@amber-ui/core/src/component/grain-container/ContainerHubColorSync';
import { ContainerTypeLegacy } from '@amber-ui/core/src/util/constant';

// Module-level variables track previous barge/acct for Zustand state reset in beforeLoad.
let previousBarge: string | undefined;
let previousAcct: string | undefined;

export const Route = createFileRoute('/_auth/_barge')({
  validateSearch: (search: Record<string, unknown>): { barge?: string; acct?: string } => ({
    barge:
      typeof search.barge === 'string' && /^[a-zA-Z0-9]{8,}$/.test(search.barge)
        ? search.barge
        : undefined,
    acct:
      typeof search.acct === 'string' && /^[a-zA-Z0-9]{8,}$/.test(search.acct)
        ? search.acct
        : undefined,
  }),
  beforeLoad: ({ search, context: { auth } }) => {
    const currentBarge = search.barge;
    const currentAcct = search.acct;

    if (previousAcct !== undefined && currentAcct !== previousAcct) {
      useBinScopedStore.getState().resetAll();
    } else if (previousBarge !== undefined && currentBarge !== previousBarge) {
      useBinScopedStore.getState().resetBinState();
    }

    previousBarge = currentBarge;
    previousAcct = currentAcct;

    if (currentBarge && !currentAcct && auth?.account_id) {
      throw redirect({
        to: '.',
        search: { ...search, acct: auth.account_id },
        replace: true,
      });
    }
  },
  component: function BargeLayout() {
    const { barge, acct } = Route.useSearch();
    const { account_id: sessionAccountId } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (barge && !acct && sessionAccountId) {
        void navigate({
          // oxlint-disable-next-line typescript/no-explicit-any -- TanStack Router's navigate generic overloads make precise typing impractical for search merge
          search: { barge, acct: sessionAccountId } as any,
          replace: true,
        });
      }
    }, [barge, acct, sessionAccountId, navigate]);

    return (
      <>
        {/* Sync hub→color mapping at the layout level so all sub-routes (dashboard,
            settings, devices, export) have activeHubsWithColor populated without per-page boilerplate.
            Renders nothing — purely a side-effect component that keeps the context in sync. */}
        {barge && (
          <ContainerHubColorSync container_id={barge} container_type={ContainerTypeLegacy.barge} />
        )}
        <Outlet />
      </>
    );
  },
});
